import type { AuthSession } from '@emazy/shared-types';
import { authSessionSchema } from '@emazy/shared-validation';

export interface AuthSessionStorage {
  read(): AuthSession | null;
  write(session: AuthSession): void;
  clear(): void;
}

export class BrowserAuthSessionStorage implements AuthSessionStorage {
  constructor(
    private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
    private readonly key: string,
  ) {}

  read(): AuthSession | null {
    try {
      const serialized = this.storage.getItem(this.key);
      if (!serialized) return null;
      const parsed = authSessionSchema.safeParse(JSON.parse(serialized));
      if (!parsed.success || new Date(parsed.data.expiresAt).getTime() <= Date.now()) {
        this.clear();
        return null;
      }
      return parsed.data;
    } catch {
      this.clear();
      return null;
    }
  }

  write(session: AuthSession): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(session));
    } catch {
      // Storage may be blocked or unavailable. The in-memory provider session remains usable.
    }
  }

  clear(): void {
    try {
      this.storage.removeItem(this.key);
    } catch {
      // Clearing a blocked storage backend is best-effort.
    }
  }
}

export function createBrowserSessionStores() {
  return {
    persistent: new BrowserAuthSessionStorage(window.localStorage, 'emazy.auth.session'),
    temporary: new BrowserAuthSessionStorage(window.sessionStorage, 'emazy.auth.session'),
  };
}
